import React, { useEffect, useRef, useState } from "react";
import api from "./api";
import { Chat, Message, User } from "./type";
import { Avatar, Badge, Box, Button, CircularProgress, Divider, Fab, IconButton, List, ListItem, ListItemAvatar, ListItemText, Paper, TextField, Tooltip, Typography } from "@material-ui/core";
import { Add, ArrowLeft, AttachFile, ChatBubbleOutline, Close, Fullscreen, Remove, Send } from "@material-ui/icons";
import { CURRENT_USER } from "./mock";
import UserList from "./userlist";
import ChatGroup from "./room";
import { Alert } from "@material-ui/lab";

const ChatRoom = () => {
    // UI State
    const [isOpen, setIsOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [activeChatId, setActiveChatId] = useState<number | null>(null);
    const [showUserSelection, setShowUserSelection] = useState(false);

    // Data State
    const [chats, setChats] = useState<Chat[]>([]);
    const [loadingChats, setLoadingChats] = useState(false);

    // --- Logic ---
    useEffect(() => {
        if (Notification.permission !== "granted" && Notification.permission !== "denied") {
            Notification.requestPermission();
        }
    }, [])

    // Initial Load & Polling for Chat List
    useEffect(() => {
        const fetchChats = async () => {
            try {
                const data = await api.getChats();
                setChats(data);
                // Check data for unread messages and change page title to show there is new messages
                const totalUnread = data.reduce((acc, chat) => acc + chat.unread_count, 0);
                if (totalUnread > 0) {
                    document.title = `(${totalUnread}) Novas Mensagems - Cidade Inteligente`;
                    if(!isOpen && Notification.permission === "granted") {
                        /* new Notification("Você tem novas mensagens!", {
                            body: `Você tem ${totalUnread} mensagens não lidas.`,
                            icon: '/favicon.ico'
                        }); */
                    }
                }else {
                    document.title = `Cidade Inteligente`;
                }
            } catch (e) {
                console.error("Failed to fetch chats", e);
            }
        };

        if (isOpen) {
            fetchChats();
            const interval = setInterval(fetchChats, 5000); // Poll every 5s
            return () => clearInterval(interval);
        }
    }, [isOpen]);

    const handleNewChatClick = async () => {
        setShowUserSelection(true);
    };

    const handleUserSelect = async (user: User) => {
        try {
            const newChat = await api.createChat(user.id);
            setChats([newChat, ...chats]);
            setShowUserSelection(false);
            setActiveChatId(newChat.id);
        } catch (e) {
            console.error("Failed to create chat", e);
        }
    };

    const activeChat = chats.find(c => c.id === activeChatId);

    // --- Render ---

    // 1. Minimized State (Floating Button)
    if (!isOpen) {
        return <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50 }}>
            <Fab
                color="primary"
                aria-label="chat"
                onClick={() => setIsOpen(true)}
            >
                <Badge badgeContent={chats.reduce((acc, c) => acc + c.unread_count, 0)} color="error">
                    <ChatBubbleOutline />
                </Badge>
            </Fab>
        </Box>
    }

    // 2. Expanded State (Widget)
    return <Box
        style={{
            transition: 'all 0.3s',
            borderTopLeftRadius: isFullScreen ? 0 : 12,
            borderTopRightRadius: isFullScreen ? 0 : 12,
            outlineColor: 'rgba(0,0,0,0.2)',
            outlineStyle: 'solid',
            outlineWidth: isFullScreen ? 0 : 1,
        }}
        sx={{
            position: 'fixed',
            zIndex: 1000,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            ...(isFullScreen ? {
                bottom: 0,
                right: 0,
                width: '100%',
                height: '100%',
            } : activeChatId ? {
                bottom: 0,
                right: 16,
                width: 600,
                height: 600
            } : {
                bottom: 0,
                right: 16,
                width: 400,
                height: 600
            })
        }}
    >
        {/* Header */}
        <Box
            sx={{
                bgcolor: 'primary.main',
                color: 'white',
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
            }}
        >
            <Box display="flex" alignItems="center" style={{ gap: '0.5rem' }}>
                {activeChatId ? <Box display="flex" alignItems="center" style={{ gap: '0.5rem' }}>
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={() => {
                            setActiveChatId(null);
                            setShowUserSelection(false);
                        }}
                    >
                        <ArrowLeft />
                    </IconButton>
                    <Avatar
                        src={activeChat?.participant.avatar_url}
                        style={{ width: 32, height: 32 }}
                    >
                        {activeChat?.participant.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box display="flex" flexDirection="column">
                        <Typography variant="subtitle2" style={{ lineHeight: 1.2 }}>
                            {activeChat?.participant.name}
                        </Typography>
                        <Typography variant="caption" style={{ opacity: 0.8, fontSize: '0.65rem', lineHeight: 1 }}>
                            {activeChat?.participant.is_online ? 'Disponível' : 'Offline'}
                        </Typography>
                    </Box>
                </Box> : showUserSelection ? <Box display="flex" alignItems="center" style={{ gap: '0.5rem' }}>
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={() => {
                            setActiveChatId(null);
                            setShowUserSelection(false);
                        }}
                    >
                        <ArrowLeft />
                    </IconButton>
                    <Typography variant="h6" style={{ fontWeight: "bold" }}>Nova Conversa</Typography>
                </Box> : <Typography variant="h6" style={{ fontWeight: "bold" }}>Mensagens</Typography>}
            </Box>

            <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => setIsFullScreen(!isFullScreen)} color="inherit">
                    {isFullScreen ? <Close fontSize="small" /> : <Fullscreen fontSize="small" />}
                </IconButton>
                {!isFullScreen ? <IconButton size="small" onClick={() => setIsOpen(false)} color="inherit">
                    <Remove fontSize="small" />
                </IconButton> : null}
            </Box>
        </Box>
        {/* Body Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>

            {/* View: User Selection for New Chat */}
            {showUserSelection ? <UserList
                handleUserSelect={handleUserSelect}
                chats={chats}
            /> : !activeChatId ? <Box sx={{ height: '100%' }}>
                {/* New Chat Button */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        startIcon={<Add />}
                        onClick={handleNewChatClick}
                    >
                        Nova Conversa
                    </Button>
                </Box>
                <List disablePadding>
                    {chats.map((chat) => (
                        <React.Fragment key={chat.id}>
                            <ListItem
                                button
                                onClick={() => setActiveChatId(chat.id)}
                                alignItems="flex-start"
                            >
                                <ListItemAvatar>
                                    <Badge
                                        color="primary"
                                        variant="dot"
                                        invisible={!chat.participant.is_online}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        overlap="circular"
                                    >
                                        <Avatar
                                            src={chat.participant.avatar_url}
                                        >
                                            {chat.participant.name?.charAt(0).toUpperCase()}
                                        </Avatar>
                                    </Badge>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>{chat.participant.name}</Typography>
                                            {chat.last_message ? <Typography variant="caption" color="textSecondary">
                                                {new Date(chat.updated_at).toLocaleDateString()}
                                            </Typography> : null}
                                        </Box>
                                    }
                                    secondary={
                                        <Typography
                                            variant="body2"
                                            color={chat.unread_count > 0 ? 'textPrimary' : 'textSecondary'}
                                            display="block"
                                            style={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                fontWeight: chat.unread_count > 0 ? 600 : 400
                                            }}
                                        >
                                            {chat.last_message ? (
                                                chat.last_message.sender_id === CURRENT_USER.id
                                                    ? `Você: ${chat.last_message.content}`
                                                    : chat.last_message.content
                                            ) : 'Sem mensagens'}
                                        </Typography>
                                    }
                                />
                                {chat.unread_count > 0 ? <Box
                                    sx={{
                                        ml: 1,
                                        width: 20,
                                        height: 20,
                                        bgcolor: 'error.main',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontSize: '0.7rem'
                                    }}
                                >
                                    {chat.unread_count}
                                </Box> : null}
                            </ListItem>
                            <Divider component="li" />
                        </React.Fragment>
                    ))}
                    {chats.length === 0 && !loadingChats ? <Box p={4} textAlign="center" color="text.secondary">
                        <Typography variant="body2">Sem conversas encontradas.</Typography>
                    </Box> : null}
                </List>
            </Box> : <ChatGroup
                activeChatId={activeChatId}
                isOpen={isOpen}
            />}
        </Box>
    </Box>
};

export default ChatRoom;